// =============================================================================
// DeliveryAddressRows.jsx — the DFV Services → Delivery Addresses list (display)
// =============================================================================
// Rendered INLINE on the ShelterDetail "Delivery Addresses" tab. This list is
// DISPLAY-ONLY (John, 8 Aug): the status indicators are NOT actionable — they just
// tell you each address's state (Default / Banned / Silent / Pending / Deactivated)
// in fixed aligned columns. The ONLY action is **Edit** (teal), which opens the
// draggable DeliveryAddressEditModal where every change (ban, silent, count,
// active/deactivated, default, address fields) is made and committed on Save.
// Edit is available on every row — including deactivated ones, so they can be
// reactivated from the modal.
// =============================================================================

// Fixed columns so the status pills + Edit line up down the list. Edit sits just to
// the LEFT of the deactivated-status column (John).
const GRID = "minmax(0,1fr) 76px 72px 64px 104px 64px 108px";

function Pill({ bg, children }) {
  return (
    <span
      className="text-[10px] font-bold text-white rounded px-[6px] py-[2px] whitespace-nowrap"
      style={{ background: bg }}
    >
      {children}
    </span>
  );
}

export function DeliveryAddressRows({ deliveryAddrs, startEditAddr }) {
  if (!deliveryAddrs || deliveryAddrs.length === 0) {
    return (
      <div className="py-[10px] px-[14px] text-[13px] text-zephyr-muted border border-zephyr-border rounded-[8px]">
        No delivery addresses yet.
      </div>
    );
  }
  return (
    <div className="border border-zephyr-border rounded-[8px] overflow-hidden">
      {deliveryAddrs.map(function (addr, idx) {
        const dz = addr.deactivated;
        return (
          <div
            key={addr.id || idx}
            className="grid items-center gap-[6px] py-[10px] px-[14px]"
            style={{
              gridTemplateColumns: GRID,
              borderTop: idx > 0 ? "1px solid #EEF3F2" : "none",
              background: dz ? "#FFF3F3" : addr.is_default ? "#E0F2F1" : "#FFFFFF",
              opacity: dz ? 0.7 : 1,
            }}
          >
            {/* Info */}
            <div style={{ minWidth: 0 }}>
              <div className="font-bold text-[13px] text-zephyr-text truncate">
                {addr.address_name}
              </div>
              <div className="text-[12px] text-zephyr-muted truncate">
                {[
                  addr.street,
                  addr.street2,
                  addr.suburb_city || addr.suburb,
                  addr.state,
                  addr.postcode,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </div>
              {addr.delivery_instruction && (
                <div className="text-[11px] text-zephyr-muted italic truncate">
                  {addr.delivery_instruction}
                </div>
              )}
            </div>

            {/* Status columns (non-actionable) */}
            <div className="flex justify-center">
              {addr.is_default && !dz && <Pill bg="#00897B">DEFAULT</Pill>}
            </div>
            <div className="flex justify-center">
              {addr.banned_address && !dz && <Pill bg="#B71C1C">BANNED</Pill>}
            </div>
            <div className="flex justify-center">
              {addr.silent_address && !dz && <Pill bg="#607D8B">SILENT</Pill>}
            </div>
            <div className="flex justify-center">
              {addr.zephyr_verified === false && !dz && (
                <Pill bg="#F57C00">PENDING ({addr.use_count || 0}/3)</Pill>
              )}
            </div>

            {/* Edit — the ONLY action; teal, just left of the deactivated status */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={function () {
                  startEditAddr(addr, idx);
                }}
                className="w-full bg-zephyr-teal text-white font-semibold border-0 rounded-[6px] py-1 px-[8px] text-[11px] cursor-pointer"
                style={{ fontFamily: "inherit" }}
              >
                Edit
              </button>
            </div>

            {/* Active / deactivated status (non-actionable) */}
            <div className="flex justify-center">{dz && <Pill bg="#C62828">DEACTIVATED</Pill>}</div>
          </div>
        );
      })}
    </div>
  );
}
